import { api } from './apiClient';

/**
 * Receivables & Collections Control Center service.
 * The apiClient auto-unwraps the `{ status, data }` envelope, so each call
 * returns `data` directly. The API is the source of truth; types are light.
 */

export interface CollectionKpis {
  totalReceivables: number;
  current: number;
  overdue: number;
  dueThisWeek: number;
  dueThisMonth: number;
  collectedThisMonth: number;
  collectionRate: number | null;
  dso: number | null;
  highRiskExposure: number;
  openInvoiceCount: number;
  receiptsThisMonth: number;
  customerCount: number;
  highRiskCount: number;
  topDebtor: { customerId: number; name: string; amount: number } | null;
}

export interface CollectionCustomer {
  customerId: number;
  customerName: string;
  gstin: string | null;
  outstanding: number;
  overdue: number;
  current: number;
  oldestDaysOverdue: number;
  creditLimit: number;
  creditDays: number;
  availableCredit: number;
  utilisationPct: number;
  lastPaymentDate: string | null;
  openInvoiceCount: number;
  openPromises: number;
  brokenPromises: number;
  risk: 'HIGH' | 'MEDIUM' | 'LOW' | 'CURRENT';
  riskScore: number;
}

export interface ControlCenter { asOf: string; kpis: CollectionKpis; customers: CollectionCustomer[] }

export interface AgingSummary {
  asOnDate: string;
  buckets: { notDue: number; d1_30: number; d31_60: number; d61_90: number; d90plus: number };
  total: number;
  reportTotals: any;
  customers: any[];
}

export interface CustomerReceipt {
  id: number;
  receiptNumber: string;
  paymentDate: string;
  customerId: number;
  customerName: string;
  invoiceId: number;
  invoiceNumber: string;
  amount: number;
  paymentMode: string;
  reference: string | null;
  journalEntryId: number | null;
}

export interface CustomerReceiptDetail {
  id: number;
  receiptNumber: string;
  paymentDate: string;
  amount: number;
  paymentMode: string;
  reference: string | null;
  notes: string | null;
  invoice: {
    id: number;
    invoiceNumber: string;
    invoiceDate: string;
    totalAmount: number;
    paidAmount: number;
    balance: number;
    status: string;
  };
  customer: {
    id: number;
    name: string;
    gstin: string | null;
  };
  gl: {
    journalEntryId: number;
    entryNumber: string;
    entryDate: string | null;
    status: string;
    lines: { accountCode: string; accountName: string; debit: number; credit: number }[];
  } | null;
}

export interface CustomerOpenInvoice {
  id: number;
  invoiceNumber: string;
  invoiceDate: string;
  dueDate: string | null;
  totalAmount: number;
  paidAmount: number;
  balance: number;
}

export const collectionsService = {
  controlCenter: (): Promise<ControlCenter> => api.get('/collections/control-center'),
  aging: (asOnDate?: string): Promise<AgingSummary> =>
    api.get('/collections/aging', { params: asOnDate ? { asOnDate } : undefined }),
  customer360: (id: number): Promise<any> => api.get(`/collections/customer/${id}/360`),

  // Customer Payments (AR receipts) list + create-workspace picker
  listReceipts: (): Promise<{ receipts: CustomerReceipt[] }> => api.get('/collections/receipts'),
  getReceipt: (id: number): Promise<{ receipt: CustomerReceiptDetail }> => api.get(`/collections/receipts/${id}`),
  getCustomerOpenInvoices: (customerId: number): Promise<{ invoices: CustomerOpenInvoice[] }> =>
    api.get(`/collections/customer/${customerId}/open-invoices`),

  recordPayment: (d: { invoiceId: number; amount: number; date?: string; method?: string; reference?: string; notes?: string }): Promise<any> =>
    api.post('/collections/record-payment', d),
  logActivity: (d: { customerId: number; invoiceId?: number | null; activityType: string; notes?: string; outcome?: string }): Promise<any> =>
    api.post('/collections/activity', d),
  createPromise: (d: { customerId: number; invoiceId?: number | null; promisedAmount: number; promisedDate: string; notes?: string }): Promise<any> =>
    api.post('/collections/promise', d),
  updatePromise: (id: number, status: string): Promise<any> =>
    api.patch(`/collections/promise/${id}`, { status }),
};

export default collectionsService;
